import type { PaidSubscriptionTier } from '@pulsify/shared/dto'
import * as Sentry from '@sentry/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import * as m from '~/paraglide/messages'
import { extractApiError, getErrorMessageFromError } from '~/shared/lib/api-errors'
import { createLogger } from '~/shared/lib/logger'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { MINUTES } from '~/shared/lib/time-constants'
import type { CreateCheckoutRequest } from '../api'
import { stripeApi } from '../api'

export function useSubscriptionStatus(businessId: string, profileId?: string) {
  return useQuery({
    queryKey: queryKey(QueryKey.SUBSCRIPTION_STATUS, businessId, profileId ?? 'organization'),
    queryFn: () => stripeApi.getSubscriptionStatus(businessId, profileId),
    enabled: !!businessId,
  })
}

const pricingLog = createLogger({
  module: 'subscription',
  action: 'fetchPricing',
})

export function usePricing() {
  return useQuery({
    queryKey: queryKey(QueryKey.SUBSCRIPTION_PRICING),
    staleTime: 10 * MINUTES,
    queryFn: async () => {
      try {
        return await stripeApi.getPricing()
      } catch (err) {
        const apiError = extractApiError(err)

        if (apiError.errorCode === 'ServiceUnavailable') {
          // 503 - Stripe is disabled on the server, expected config state.
          pricingLog.warn(
            { errorCode: apiError.errorCode, status: apiError.statusCode },
            'Stripe pricing unavailable - service disabled on server',
          )
        } else if (apiError.errorCode === 'InternalServerError') {
          pricingLog.error(apiError, 'Failed to fetch Stripe pricing')
          Sentry.captureException(apiError, {
            extra: {
              errorCode: apiError.errorCode,
              statusCode: apiError.statusCode,
              module: 'subscription',
              action: 'fetchPricing',
            },
          })
        }

        toast.error(apiError.message)
        throw apiError
      }
    },
  })
}

export function useCreatePortalSession(businessId: string | undefined, profileId?: string) {
  return useMutation({
    mutationFn: () => {
      if (!businessId)
        throw new Error('[useCreatePortalSession] businessId is required but was not provided')
      return stripeApi.createPortalSession(businessId, window.location.href, profileId)
    },
    onSuccess: (data) => {
      window.location.href = data.url
    },
    onError: (error) => {
      toast.error(getErrorMessageFromError(error))
    },
  })
}

export function useCancelSubscription(businessId: string | undefined, profileId?: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => {
      if (!businessId)
        throw new Error('[useCancelSubscription] businessId is required but was not provided')
      return stripeApi.cancelSubscription(businessId, profileId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.SUBSCRIPTION_STATUS, businessId),
      })
      toast.success(m.subscriptionCanceled())
    },
    onError: (error) => {
      toast.error(getErrorMessageFromError(error))
    },
  })
}

export function useResumeSubscription(businessId: string | undefined, profileId?: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => {
      if (!businessId)
        throw new Error('[useResumeSubscription] businessId is required but was not provided')
      return stripeApi.resumeSubscription(businessId, profileId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.SUBSCRIPTION_STATUS, businessId),
      })
      toast.success(m.subscriptionResumed())
    },
    onError: (error) => {
      toast.error(getErrorMessageFromError(error))
    },
  })
}

export function useTrialSubscription(businessId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (vars: { productType: PaidSubscriptionTier; durationDays: number }) => {
      if (!businessId)
        throw new Error('[useTrialSubscription] businessId is required but was not provided')
      return stripeApi.trialSubscription({ businessId, ...vars })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKey(QueryKey.SUBSCRIPTION_STATUS, businessId),
      })
      toast.success(m.trialActivated())
    },
    onError: (error) => {
      toast.error(getErrorMessageFromError(error))
    },
  })
}

export function useCreateCheckout(onInlineSuccess?: () => void) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateCheckoutRequest) => stripeApi.createCheckout(request),
    onSuccess: (data, variables) => {
      if (data.url) {
        window.location.href = data.url
      } else {
        queryClient.invalidateQueries({
          queryKey: queryKey(QueryKey.SUBSCRIPTION),
        })
        queryClient.invalidateQueries({
          queryKey: queryKey(QueryKey.CREDITS_BALANCE, variables.businessId),
        })
        onInlineSuccess ? onInlineSuccess() : toast.success(m.planUpgraded())
      }
    },
    onError: (error) => {
      toast.error(getErrorMessageFromError(error))
    },
  })
}
